import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { CreditCard, Loader2, CheckCircle2, XCircle, Clock, AlertCircle } from 'lucide-react';
import { getBackendUrl } from './utils/api';

const API = `${getBackendUrl()}/api`;

/**
 * MPCardCheckout - Checkout Transparente do Mercado Pago para Cartão
 * 
 * Carrega o SDK MercadoPago.js v2 do CDN, tokeniza o cartão no navegador,
 * e envia o token + dados pro backend cobrar.
 * 
 * Props:
 *   - subdomain: string (ex: "minhalanchonete")
 *   - amount: number (valor total do pedido em reais)
 *   - orderId: string (id do pedido já criado)
 *   - payerEmail: string
 *   - payerName?: string
 *   - onSuccess(payment): pedido pago
 *   - onPending(payment): em análise
 *   - onError(message): falha
 */
export default function MPCardCheckout({
  subdomain,
  amount,
  orderId,
  payerEmail,
  payerName = '',
  onSuccess,
  onPending,
  onError,
  onCancel,
}) {
  const [config, setConfig] = useState(null);
  const [mpReady, setMpReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const [cardNumber, setCardNumber] = useState('');
  const [cardholderName, setCardholderName] = useState(payerName || '');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [docNumber, setDocNumber] = useState('');
  const [emailInput, setEmailInput] = useState(payerEmail || '');
  const [installments, setInstallments] = useState(1);
  // Tipo de cartão escolhido pelo cliente: 'credit' ou 'debit'
  const [cardType, setCardType] = useState('credit');
  const [installmentOptions, setInstallmentOptions] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [isDebit, setIsDebit] = useState(false);
  const mpRef = useRef(null);

  // 1) Buscar config pública + carregar SDK do MP
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/mercadopago/public/${subdomain}/card-config`);
        if (!mounted) return;
        if (!r.data.card_online_enabled || !r.data.public_key) {
          setErrorMsg('Cartão online não está habilitado nesta loja');
          setLoading(false);
          return;
        }
        setConfig(r.data);

        // Auto-fix do cardType conforme config do lojista
        if (!r.data.credit_enabled && r.data.debit_enabled) {
          setCardType('debit');
        } else if (r.data.credit_enabled && !r.data.debit_enabled) {
          setCardType('credit');
        }

        // Load MercadoPago.js v2
        if (window.MercadoPago) {
          mpRef.current = new window.MercadoPago(r.data.public_key, { locale: 'pt-BR' });
          setMpReady(true);
          setLoading(false);
          return;
        }
        const script = document.createElement('script');
        script.src = 'https://sdk.mercadopago.com/js/v2';
        script.async = true;
        script.onload = () => {
          if (window.MercadoPago && mounted) {
            mpRef.current = new window.MercadoPago(r.data.public_key, { locale: 'pt-BR' });
            setMpReady(true);
          }
          if (mounted) setLoading(false);
        };
        script.onerror = () => {
          if (mounted) {
            setErrorMsg('Falha ao carregar Mercado Pago');
            setLoading(false);
          }
        };
        document.body.appendChild(script);
      } catch (e) {
        if (mounted) {
          setErrorMsg('Erro ao carregar configuração de pagamento');
          setLoading(false);
        }
      }
    })();
    return () => { mounted = false; };
  }, [subdomain]);

  // 2) Detectar bandeira ao digitar 6+ dígitos
  useEffect(() => {
    const digits = cardNumber.replace(/\D/g, '');
    if (digits.length >= 6 && config?.public_key) {
      const localPm = detectBrandLocally(digits);
      if (localPm) {
        // Se cliente escolheu débito, ajustar payment_method_id
        const adjustedPm = applyCardType(localPm, cardType);
        setPaymentMethod(adjustedPm);
        setIsDebit(cardType === 'debit');
        if (cardType === 'debit') {
          setInstallments(1);
          setInstallmentOptions([{
            installments: 1,
            label: `À vista R$ ${amount.toFixed(2).replace('.', ',')} (débito)`,
            installment_amount: amount, total_amount: amount, has_interest: false,
          }]);
        } else {
          setInstallmentOptions([{
            installments: 1,
            label: `À vista R$ ${amount.toFixed(2).replace('.', ',')} sem juros`,
            installment_amount: amount, total_amount: amount, has_interest: false,
          }]);
        }
      }
      const bin = digits.slice(0, 8);
      detectPaymentMethod(bin);
    } else if (digits.length < 6) {
      setPaymentMethod(null);
      setInstallmentOptions([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardNumber, config, cardType]);

  /**
   * Ajusta payment_method_id conforme tipo escolhido pelo cliente.
   * Ex: visa → debvisa, master → debmaster (quando cliente escolhe débito).
   */
  const applyCardType = (pm, type) => {
    if (!pm) return null;
    const id = pm.id;
    if (type === 'debit') {
      // Mapear bandeiras conhecidas pra equivalente de débito
      const debitMap = {
        visa: 'debvisa',
        master: 'debmaster',
        elo: 'debelo',
        amex: 'amex',  // Amex débito é raro; mantém
      };
      return { ...pm, id: debitMap[id] || id, payment_type_id: 'debit_card' };
    }
    // Crédito: garantir que não fique com prefixo deb*
    const cleanId = id?.replace(/^deb/, '') || id;
    return { ...pm, id: cleanId, payment_type_id: 'credit_card' };
  };

  /**
   * Detecção LOCAL de bandeira pelo BIN (fallback offline).
   * Garante que o botão fique habilitado mesmo se MP API falhar.
   */
  const detectBrandLocally = (digits) => {
    if (!digits || digits.length < 6) return null;
    const d = digits.replace(/\D/g, '');
    let id = null;
    let name = null;
    // Visa
    if (/^4/.test(d)) { id = 'visa'; name = 'Visa'; }
    // Master
    else if (/^(5[1-5]|222[1-9]|22[3-9]|2[3-6]|27[01]|2720)/.test(d)) { id = 'master'; name = 'Mastercard'; }
    // Elo (faixas comuns)
    else if (/^(40117[89]|43(1274|8935)|45(1416|7393|763[12])|504175|627780|636297|63636[89]|65(003[^4]|0[45][0-9]{2}|41[0-9]|54[0-9]|7[0-9]{3}|8[0-9]{3}|9[2-9][0-9]{2}))/.test(d)) { id = 'elo'; name = 'Elo'; }
    // Amex
    else if (/^3[47]/.test(d)) { id = 'amex'; name = 'American Express'; }
    // Hipercard
    else if (/^(606282|3841)/.test(d)) { id = 'hipercard'; name = 'Hipercard'; }
    // Diners
    else if (/^3(0[0-5]|[68])/.test(d)) { id = 'diners'; name = 'Diners'; }

    if (!id) return null;
    return {
      id,
      name,
      payment_type_id: 'credit_card',  // assume crédito por padrão (BIN nem sempre revela)
      issuer: null,
    };
  };

  const detectPaymentMethod = async (bin) => {
    try {
      const r = await axios.get(
        `${API}/mercadopago/public/${subdomain}/payment-methods?bin=${bin}`
      );
      const methods = r.data.results || [];
      if (methods.length > 0) {
        // Pegar método que corresponde ao tipo escolhido (crédito ou débito)
        const targetType = cardType === 'debit' ? 'debit_card' : 'credit_card';
        const matching = methods.find(m => m.payment_type_id === targetType) || methods[0];
        const adjusted = applyCardType(matching, cardType);
        setPaymentMethod(adjusted);
        if (cardType === 'credit' && matching.payment_type_id === 'credit_card') {
          loadInstallments(bin, matching.id);
        }
      }
    } catch (e) {
      // Silencioso - já temos detecção local como fallback
      console.debug('MP payment-methods API indisponível, usando detecção local');
    }
  };

  const loadInstallments = async (bin, paymentMethodId) => {
    try {
      const r = await axios.get(
        `${API}/mercadopago/public/${subdomain}/installments?bin=${bin}&amount=${amount}&payment_method_id=${paymentMethodId}`
      );
      setInstallmentOptions(r.data.installments || []);
      if ((r.data.installments || []).length > 0) {
        setInstallments(r.data.installments[0].installments);
      }
    } catch (e) {
      // fallback simples
      setInstallmentOptions([{
        installments: 1, label: `1x R$ ${amount.toFixed(2).replace('.', ',')} sem juros`,
        installment_amount: amount, total_amount: amount, has_interest: false
      }]);
    }
  };

  const formatCardNumber = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 19);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
  };
  const formatExpiry = (val) => {
    const d = val.replace(/\D/g, '').slice(0, 4);
    if (d.length <= 2) return d;
    return d.slice(0, 2) + '/' + d.slice(2);
  };
  const formatDoc = (val) => {
    const d = val.replace(/\D/g, '').slice(0, 14);
    if (d.length <= 11) {
      return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!mpRef.current) {
      setErrorMsg('Mercado Pago ainda carregando, aguarde');
      return;
    }
    if (!paymentMethod) {
      setErrorMsg('Bandeira do cartão não identificada');
      return;
    }

    // Validar email (MP exige email válido no payer)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailInput || !emailRegex.test(emailInput.trim())) {
      setErrorMsg('Informe um e-mail válido para o pagamento');
      return;
    }

    setSubmitting(true);
    setErrorMsg(null);

    try {
      // Tokenizar cartão (NÃO mandamos número do cartão pro nosso backend)
      const [expMonth, expYear] = expiry.split('/');
      const cardData = {
        cardNumber: cardNumber.replace(/\s/g, ''),
        cardholderName: cardholderName,
        cardExpirationMonth: expMonth,
        cardExpirationYear: expYear?.length === 2 ? '20' + expYear : expYear,
        securityCode: cvv,
        identificationType: docNumber.replace(/\D/g, '').length > 11 ? 'CNPJ' : 'CPF',
        identificationNumber: docNumber.replace(/\D/g, ''),
      };

      const tokenResp = await mpRef.current.createCardToken(cardData);
      if (!tokenResp || !tokenResp.id) {
        throw new Error('Falha ao tokenizar cartão. Verifique os dados.');
      }

      // Cobrar via backend
      const idempotencyKey = `${orderId || 'pay'}-${Date.now()}`;
      const r = await axios.post(
        `${API}/mercadopago/public/${subdomain}/create-card-payment`,
        {
          subdomain,
          token: tokenResp.id,
          payment_method_id: paymentMethod.id,
          issuer_id: paymentMethod.issuer?.id ? String(paymentMethod.issuer.id) : null,
          installments: parseInt(installments),
          amount: parseFloat(amount),
          payer_email: emailInput.trim(),
          payer_doc_type: cardData.identificationType,
          payer_doc_number: cardData.identificationNumber,
          payer_first_name: cardholderName.split(' ')[0],
          payer_last_name: cardholderName.split(' ').slice(1).join(' '),
          description: `Pedido ${orderId || ''}`.trim(),
          order_id: orderId || null,
        },
        { headers: { 'X-Idempotency-Key': idempotencyKey } }
      );

      if (r.data.success && r.data.status === 'approved') {
        onSuccess && onSuccess(r.data);
      } else if (r.data.success && r.data.status === 'in_process') {
        onPending && onPending(r.data);
      } else {
        const msg = r.data.error_message || 'Pagamento recusado';
        setErrorMsg(msg);
        onError && onError(msg);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Erro ao processar pagamento';
      setErrorMsg(msg);
      onError && onError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }
  if (errorMsg && !config) {
    return (
      <div className="p-6 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm flex gap-3">
        <AlertCircle className="w-5 h-5 flex-shrink-0" />
        <div>{errorMsg}</div>
      </div>
    );
  }

  const brandLabel = paymentMethod?.id?.toUpperCase() || '';
  const showJurosNote = config?.interest_payer === 'customer';
  const creditEnabled = config?.credit_enabled !== false;
  const debitEnabled = config?.debit_enabled !== false;

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="mp-card-checkout">
      <div className="flex items-center gap-2 text-white">
        <CreditCard className="w-5 h-5 text-blue-400" />
        <h3 className="font-semibold">Pagamento com Cartão</h3>
        {brandLabel && (
          <span className="ml-auto px-2 py-0.5 bg-blue-500/20 text-blue-300 text-xs rounded">
            {brandLabel}
          </span>
        )}
      </div>

      {/* Seletor Crédito / Débito */}
      {(creditEnabled && debitEnabled) && (
        <div className="grid grid-cols-2 gap-2" data-testid="card-type-selector">
          <button
            type="button"
            onClick={() => setCardType('credit')}
            data-testid="select-credit"
            className={`p-3 rounded-lg border transition flex flex-col items-center gap-1 ${
              cardType === 'credit'
                ? 'bg-blue-500/20 border-blue-500 text-white'
                : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
            }`}
          >
            <CreditCard className="w-5 h-5" />
            <span className="text-sm font-medium">Crédito</span>
            <span className="text-xs opacity-70">Pode parcelar</span>
          </button>
          <button
            type="button"
            onClick={() => setCardType('debit')}
            data-testid="select-debit"
            className={`p-3 rounded-lg border transition flex flex-col items-center gap-1 ${
              cardType === 'debit'
                ? 'bg-green-500/20 border-green-500 text-white'
                : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
            }`}
          >
            <CreditCard className="w-5 h-5" />
            <span className="text-sm font-medium">Débito</span>
            <span className="text-xs opacity-70">À vista</span>
          </button>
        </div>
      )}

      <div>
        <label className="text-xs text-gray-400 mb-1 block">Número do cartão</label>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="cc-number"
          required
          value={cardNumber}
          onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
          placeholder="1234 5678 9012 3456"
          data-testid="card-number"
          className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white font-mono"
        />
      </div>

      <div>
        <label className="text-xs text-gray-400 mb-1 block">Nome impresso no cartão</label>
        <input
          type="text"
          autoComplete="cc-name"
          required
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value.toUpperCase())}
          placeholder="JOAO SILVA"
          data-testid="card-holder"
          className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Validade (MM/AA)</label>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="cc-exp"
            required
            value={expiry}
            onChange={(e) => setExpiry(formatExpiry(e.target.value))}
            placeholder="12/30"
            maxLength={5}
            data-testid="card-expiry"
            className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white font-mono"
          />
        </div>
        <div>
          <label className="text-xs text-gray-400 mb-1 block">CVV</label>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="cc-csc"
            required
            value={cvv}
            onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="123"
            maxLength={4}
            data-testid="card-cvv"
            className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white font-mono"
          />
        </div>
      </div>

      <div>
        <label className="text-xs text-gray-400 mb-1 block">CPF/CNPJ do titular</label>
        <input
          type="text"
          inputMode="numeric"
          required
          value={docNumber}
          onChange={(e) => setDocNumber(formatDoc(e.target.value))}
          placeholder="000.000.000-00"
          data-testid="card-doc"
          className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white font-mono"
        />
      </div>

      <div>
        <label className="text-xs text-gray-400 mb-1 block">
          E-mail para envio do comprovante
        </label>
        <input
          type="email"
          autoComplete="email"
          required
          value={emailInput}
          onChange={(e) => setEmailInput(e.target.value.trim())}
          placeholder="seuemail@exemplo.com"
          data-testid="card-email"
          className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white"
        />
      </div>

      {!isDebit && installmentOptions.length > 0 && (
        <div>
          <label className="text-xs text-gray-400 mb-1 block">Parcelamento</label>
          <select
            value={installments}
            onChange={(e) => setInstallments(parseInt(e.target.value))}
            data-testid="card-installments"
            className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white"
          >
            {installmentOptions.map((opt) => (
              <option key={opt.installments} value={opt.installments} className="bg-gray-800">
                {opt.label}
              </option>
            ))}
          </select>
          {showJurosNote && (
            <p className="text-xs text-yellow-400/80 mt-1">
              💡 Parcelas com juros — taxa do cartão repassada pelo lojista
            </p>
          )}
        </div>
      )}

      {errorMsg && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-300 text-sm flex gap-2">
          <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>{errorMsg}</div>
        </div>
      )}

      <div className="flex gap-3 pt-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-3 bg-white/5 hover:bg-white/10 text-gray-300 rounded-lg"
          >
            Voltar
          </button>
        )}
        <button
          type="submit"
          disabled={submitting || !mpReady || !paymentMethod}
          data-testid="card-pay-submit"
          className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-lg font-semibold disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Processando…
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4" />
              Pagar R$ {amount.toFixed(2).replace('.', ',')}
            </>
          )}
        </button>
      </div>

      <p className="text-xs text-gray-500 text-center">
        🔒 Pagamento processado com segurança via Mercado Pago
      </p>
    </form>
  );
}
